// src/rms/utils/orgResolution.js
/**
 * Org resolution (multi-tenant) for RMS
 * -------------------------------------
 * What:
 *   Derive the current RMS organisation ID (rms_organisations doc ID) from:
 *   1) Explicit query param (?orgId=...), then
 *   2) Hostname mapping (env or defaults), then
 *   3) A safe default org ID.
 *
 * Why:
 *   - RMS is multi-org; public routes must render the correct schema/branding.
 *   - We avoid coupling to subdomain patterns and allow host -> org mapping per env.
 *
 * Notes:
 *   - orgId must be a Firestore document ID under rms_organisations/{orgId}
 *     in the codified format RMS-ORG-XXXXX.
 *   - You can override mapping via VITE_RMS_ORG_MAP (JSON string).
 *   - Keep defaults for localhost/staging to speed up development.
 */
import { RMS_DEFAULT_ORG_ID, RMS_ORG_MAP } from '../constants/env';

function normalizeHost(host) {
  if (!host) return '';
  // Remove protocol if present
  let h = host.replace(/^https?:\/+/, '');
  // Drop trailing slashes
  h = h.replace(/\/+$/, '');
  // Strip www.
  h = h.replace(/^www\./, '');
  return h;
}

function getQueryParamOrgId() {
  try {
    const search = typeof window !== 'undefined' ? window.location.search : '';
    const params = new URLSearchParams(search);
    const orgId = params.get('orgId');
    return orgId && orgId.trim().length > 0 ? orgId.trim() : null;
  } catch (e) {
    return null;
  }
}

export function resolveOrgId() {
  // 1) Allow explicit override via query param for testing/support links
  const qp = getQueryParamOrgId();
  if (qp) return qp;

  // 2) Use host mapping (prefer host:port; then host without port)
  if (typeof window !== 'undefined') {
    const fullHost = normalizeHost(window.location.host);
    if (RMS_ORG_MAP[fullHost]) return RMS_ORG_MAP[fullHost];

    // Try hostname (drop port)
    const hostnameOnly = normalizeHost(window.location.hostname);
    if (RMS_ORG_MAP[hostnameOnly]) return RMS_ORG_MAP[hostnameOnly];
  }

  // 3) Fallback default
  return RMS_DEFAULT_ORG_ID;
}

export function getOrgConfig() {
  const orgId = resolveOrgId();
  return { orgId };
}


